import {ReactElement, useEffect, useMemo, useState} from "react";
import {Button} from "@zendeskgarden/react-buttons";
import {Checkbox, Field, Label, MediaInput} from "@zendeskgarden/react-forms";
import {ReactComponent as SearchIcon} from "@zendeskgarden/svg-icons/src/16/search-stroke.svg";
import {ReactComponent as AssignIcon} from "@zendeskgarden/svg-icons/src/16/chevron-double-right-fill.svg";
import {ReactComponent as UnassignIcon} from "@zendeskgarden/svg-icons/src/16/chevron-double-left-fill.svg";
import {Inline} from "@zendeskgarden/react-loaders";
import {EntityType, FinancialEntityType, useSetGprEntitiesMutation} from "../../../__generated__/generated_types";
import {FinancialEntity, useChartOfAccounts} from "../../../contexts/chartofaccounts/ChartOfAccountsContext";
import {toast} from "react-toastify";
import {COLORS} from "../../../constants/Colors";
import {
    entityTypeLabel,
    financialEntityTypeToEntityType
} from "./logic";
import * as css from "./styles/gprEntityStyles.module.scss";
import {formatterGlName} from "../../../utils/formatters";
import {useMultiSelect} from "../../../utils/hooks/useMultiSelect";

interface CoaItem {
    id: string;
    displayName: string;
    type: EntityType;
}

interface GPREGroupAccountsViewProps {
    group: {
        id?: string | null;
        name: string;
        gprEntities: {id: string; type: EntityType}[];
    };
    budgetYear: number;
    closed: (changed: boolean) => void;
}

export function GPREGroupAccountsView(props: GPREGroupAccountsViewProps): ReactElement {
    const {group, budgetYear} = props;
    const {chartOfAccountsFlat} = useChartOfAccounts();
    const [setGprEntities] = useSetGprEntitiesMutation();
    const [saving, setSaving] = useState(false);

    const allItems = useMemo(() => {
        const fromCoa = (chartOfAccountsFlat ?? [])
            .filter(entity =>
                entity.type === FinancialEntityType.Account ||
                entity.type === FinancialEntityType.Category
            )
            .map(toCoaItem);
        const knownKeys = new Set(fromCoa.map(entityKey));
        const orphans = group.gprEntities
            .filter(entity => !knownKeys.has(entityKey(entity)))
            .map(entity => ({
                id: entity.id,
                displayName: entity.id,
                type: entity.type
            }));
        return [...fromCoa, ...orphans];
    }, [chartOfAccountsFlat, group.gprEntities]);

    const originalAssignedKeys = useMemo(() => {
        return new Set(group.gprEntities.map(entityKey));
    }, [group]);

    const [assignedItems, setAssignedItems] = useState<CoaItem[]>(() => {
        return allItems.filter(item => originalAssignedKeys.has(entityKey(item)));
    });
    const [availableItems, setAvailableItems] = useState<CoaItem[]>(() => {
        return allItems.filter(item => !originalAssignedKeys.has(entityKey(item)));
    });

    const [availableSearch, setAvailableSearch] = useState("");
    const [assignedSearch, setAssignedSearch] = useState("");
    const [filteredAvailable, setFilteredAvailable] = useState<CoaItem[]>(availableItems);
    const [filteredAssigned, setFilteredAssigned] = useState<CoaItem[]>(assignedItems);
    const availableSelection = useMultiSelect(filteredAvailable, entityKey);
    const assignedSelection = useMultiSelect(filteredAssigned, entityKey);
    const selectedAvailable = availableSelection.selected;
    const selectedAssigned = assignedSelection.selected;

    useEffect(() => {
        const originalKeys = new Set(group.gprEntities.map(entityKey));
        setAssignedItems(allItems.filter(item => originalKeys.has(entityKey(item))));
        setAvailableItems(allItems.filter(item => !originalKeys.has(entityKey(item))));
        availableSelection.setSelected([]);
        assignedSelection.setSelected([]);
    }, [allItems, group]);

    useEffect(() => {
        if(availableSearch === "") {
            setFilteredAvailable(availableItems);
        } else {
            const search = availableSearch.toLowerCase();
            setFilteredAvailable(availableItems.filter(item =>
                item.displayName.toLowerCase().includes(search) ||
                entityTypeLabel(item.type).toLowerCase().includes(search)
            ));
        }
    }, [availableSearch, availableItems]);

    useEffect(() => {
        if(assignedSearch === "") {
            setFilteredAssigned(assignedItems);
        } else {
            const search = assignedSearch.toLowerCase();
            setFilteredAssigned(assignedItems.filter(item =>
                item.displayName.toLowerCase().includes(search) ||
                entityTypeLabel(item.type).toLowerCase().includes(search)
            ));
        }
    }, [assignedSearch, assignedItems]);

    function assignItems(): void {
        availableSelection.setSelected([]);
        assignedSelection.setSelected(prev => [...selectedAvailable, ...prev]);
        setAvailableItems(prev => prev.filter(item => !selectedAvailable.some(x => entityKey(x) === entityKey(item))));
        setAssignedItems(prev => [...selectedAvailable, ...prev]);
    }

    function unassignItems(): void {
        availableSelection.setSelected(prev => [...selectedAssigned, ...prev]);
        assignedSelection.setSelected([]);
        setAvailableItems(prev => [...selectedAssigned, ...prev]);
        setAssignedItems(prev => prev.filter(item => !selectedAssigned.some(x => entityKey(x) === entityKey(item))));
    }

    function doSave(): void {
        const currentAssignedKeys = new Set(assignedItems.map(entityKey));
        const unchanged = currentAssignedKeys.size === originalAssignedKeys.size
            && Array.from(originalAssignedKeys).every(key => currentAssignedKeys.has(key));
        if(unchanged) {
            props.closed(false);
            return;
        }

        setSaving(true);
        setGprEntities({
            variables: {
                budgetYear,
                groupId: group.id,
                entities: assignedItems.map(item => ({id: item.id, type: item.type}))
            }
        }).then(() => {
            toast.success("Accounts updated!");
            props.closed(true);
        }).catch(() => {/* Do Nothing */})
            .finally(() => setSaving(false));
    }

    return <>
        <div className={css.tableHeaderActions}>
            <h5 className={css.tableHeaderActionsLeft}>
                Accounts for &quot;{group.name}&quot;
                {saving && <Inline size={24} color={COLORS.PRIMARY_500} aria-label="loading"/>}
            </h5>
            <div className={css.tableHeaderActionsRight}>
                <Button onClick={() => props.closed(false)}>Cancel</Button>
                <Button isPrimary className={css.tableHeaderActionsButton} disabled={saving} onClick={doSave}>
                    Save
                </Button>
            </div>
        </div>
        <div className={css.manageViewBody}>
            <div className={css.dualListContainer}>
                <div className={css.dualListColumn}>
                    <div className={css.dualListHeader}>
                        <div className={css.dualListHeaderContent}>
                            <h4>Available</h4>
                        </div>
                        <Field>
                            <Label hidden>Search Available</Label>
                            <MediaInput
                                type="text"
                                placeholder="Search accounts or categories..."
                                isCompact
                                start={<SearchIcon />}
                                value={availableSearch}
                                onChange={e => setAvailableSearch(e.target.value)}
                            />
                        </Field>
                        <div className={css.selectionStatusContainer}>
                            <Field>
                                <Checkbox
                                    checked={availableSelection.allSelected}
                                    indeterminate={availableSelection.someSelected}
                                    onChange={e => availableSelection.selectAll(e.target.checked)}
                                >
                                    <Label>
                                        <span>{selectedAvailable.length} of {availableItems.length} selected</span>
                                    </Label>
                                </Checkbox>
                            </Field>
                        </div>
                    </div>
                    <div className={css.dualListItems}>
                        {filteredAvailable.map((item, index) =>
                            <div key={entityKey(item)} className={css.listItem}>
                                <Field>
                                    <Checkbox
                                        checked={availableSelection.isSelected(item)}
                                        onClick={event => availableSelection.handleSelect(event.currentTarget.checked, item, index, event.shiftKey)}
                                        onChange={() => undefined}
                                    >
                                        <Label>
                                            <span className={css.itemName}>{item.displayName}</span>
                                            <span className={css.itemMeta}>{entityTypeLabel(item.type)}</span>
                                        </Label>
                                    </Checkbox>
                                </Field>
                            </div>
                        )}
                    </div>
                </div>
                <div className={css.middleButtons}>
                    <Button onClick={assignItems}><AssignIcon /></Button>
                    <Button onClick={unassignItems}><UnassignIcon /></Button>
                </div>
                <div className={css.dualListColumn}>
                    <div className={css.dualListHeader}>
                        <div className={css.dualListHeaderContent}>
                            <h4>Assigned</h4>
                        </div>
                        <Field>
                            <Label hidden>Search Assigned</Label>
                            <MediaInput
                                type="text"
                                placeholder="Search..."
                                isCompact
                                start={<SearchIcon />}
                                value={assignedSearch}
                                onChange={e => setAssignedSearch(e.target.value)}
                            />
                        </Field>
                        <div className={css.selectionStatusContainer}>
                            <Field>
                                <Checkbox
                                    checked={assignedSelection.allSelected}
                                    indeterminate={assignedSelection.someSelected}
                                    onChange={e => assignedSelection.selectAll(e.target.checked)}
                                >
                                    <Label>
                                        <span>{selectedAssigned.length} of {assignedItems.length} selected</span>
                                    </Label>
                                </Checkbox>
                            </Field>
                        </div>
                    </div>
                    <div className={css.dualListItems}>
                        {filteredAssigned.map((item, index) =>
                            <div key={entityKey(item)} className={css.listItem}>
                                <Field>
                                    <Checkbox
                                        checked={assignedSelection.isSelected(item)}
                                        onClick={event => assignedSelection.handleSelect(event.currentTarget.checked, item, index, event.shiftKey)}
                                        onChange={() => undefined}
                                    >
                                        <Label>
                                            <span className={css.itemName}>{item.displayName}</span>
                                            <span className={css.itemMeta}>{entityTypeLabel(item.type)}</span>
                                        </Label>
                                    </Checkbox>
                                </Field>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </>;
}

function toCoaItem(entity: FinancialEntity): CoaItem {
    return {
        id: entity.id,
        displayName: formatterGlName(entity.name, entity.number),
        type: financialEntityTypeToEntityType(entity.type)
    };
}

function entityKey(item: {id: string; type: EntityType}): string {
    return `${item.type}:${item.id}`;
}
